import React from 'react'
import { Link } from 'react-router-dom'
import '../App.css'

function PrivacyPolicy() {
  return (
    <div className='privacy-policy-container'>
      <div className='privacy-policy-nav'>
        <Link to='/' className='privacy-back-link'>← Back to Home</Link>
      </div>
      <div className='privacy-policy-content'>
        <h1>Privacy Policy</h1>
        <p className='last-updated'>Last Updated: {new Date().toLocaleDateString()}</p>
        
        <section className='privacy-section'>
          <h2>1. Introduction</h2>
          <p>
            Welcome to Fashioniste ("we," "our," or "us"). This Privacy Policy explains how we collect, 
            use, disclose, and safeguard your information when you use our OOTD (Outfit of the Day) 
            and fashion journal application. Please read this privacy policy carefully. If you do 
            not agree with the terms of this privacy policy, please do not access the application.
          </p>
        </section>

        <section className='privacy-section'>
          <h2>2. Information We Collect</h2>
          
          <h3>2.1 Information You Provide to Us</h3>
          <ul>
            <li><strong>Photos and Images:</strong> When you upload your OOTD photos, outfit images, 
            and fashion-related content, we collect and store these images on our servers.</li>
            <li><strong>Account Information:</strong> Name, email address, username, profile picture, 
            and any other information you choose to provide.</li>
            <li><strong>Content:</strong> Captions, comments, style descriptions, fashion preferences, 
            and any other content you post or share through the application.</li>
            <li><strong>Profile Information:</strong> Bio, location, fashion interests, and other 
            profile details you choose to share.</li>
          </ul>

          <h3>2.2 Automatically Collected Information</h3>
          <ul>
            <li><strong>Usage Data:</strong> How you interact with the application, features you use, 
            and time spent on the platform.</li>
            <li><strong>Device Information:</strong> Device type, operating system, unique device 
            identifiers, and mobile network information.</li>
            <li><strong>Log Data:</strong> IP address, browser type, access times, and pages viewed.</li>
            <li><strong>Location Data:</strong> If you enable location services, we may collect 
            information about your location.</li>
          </ul>
        </section>

        <section className='privacy-section'>
          <h2>3. How We Use Your Information</h2>
          <p>We use the information we collect for various purposes, including:</p>
          <ul>
            <li><strong>Service Provision:</strong> To provide, maintain, and improve our services, 
            including displaying your OOTD posts and fashion content.</li>
            <li><strong>Model Training and AI Development:</strong> Your uploaded photos and images 
            may be used to train, develop, and improve machine learning models, artificial intelligence 
            systems, and computer vision technologies. This includes but is not limited to:
              <ul>
                <li>Fashion recognition and classification</li>
                <li>Style analysis and recommendations</li>
                <li>Image processing and enhancement</li>
                <li>Trend detection and analysis</li>
              </ul>
            </li>
            <li><strong>Personalization:</strong> To personalize your experience, provide style 
            recommendations, and show you relevant content.</li>
            <li><strong>Communication:</strong> To send you updates, notifications, and respond to 
            your inquiries.</li>
            <li><strong>Analytics:</strong> To analyze usage patterns, improve our services, and 
            conduct research.</li>
            <li><strong>Safety and Security:</strong> To detect, prevent, and address technical issues, 
            fraud, or abuse.</li>
          </ul>
        </section>

        <section className='privacy-section'>
          <h2>4. How We Share Your Information</h2>
          
          <h3>4.1 Third-Party Service Providers</h3>
          <p>
            We may share your information, including photos and images, with third-party service 
            providers who perform services on our behalf, such as:
          </p>
          <ul>
            <li>Cloud storage providers</li>
            <li>Analytics services</li>
            <li>Machine learning and AI technology partners</li>
            <li>Content delivery networks</li>
            <li>Customer support services</li>
          </ul>

          <h3>4.2 Third-Party Partners for Model Training</h3>
          <p>
            Your uploaded photos and images may be shared with third-party partners, including 
            research institutions, technology companies, and AI development organizations, for the 
            purpose of:
          </p>
          <ul>
            <li>Training and developing machine learning models</li>
            <li>Improving AI and computer vision technologies</li>
            <li>Conducting fashion and style research</li>
            <li>Developing new features and capabilities</li>
          </ul>
          <p>
            <strong>Important:</strong> By using Fashioniste, you consent to the use of your photos and 
            images for these purposes, including sharing with third parties for model training and 
            AI development.
          </p>

          <h3>4.3 Public Content</h3>
          <p>
            Content you choose to make public (such as public OOTD posts) may be visible to other 
            users and may be indexed by search engines.
          </p>

          <h3>4.4 Legal Requirements</h3>
          <p>
            We may disclose your information if required by law, court order, or government regulation, 
            or if we believe disclosure is necessary to protect our rights, property, or safety, or 
            that of our users or others.
          </p>

          <h3>4.5 Business Transfers</h3>
          <p>
            In the event of a merger, acquisition, or sale of assets, your information may be transferred 
            to the acquiring entity.
          </p>
        </section>

        <section className='privacy-section'>
          <h2>5. Data Storage and Security</h2>
          <p>
            Your photos and information are stored on our secure servers and may be stored in 
            databases located in various jurisdictions. We implement appropriate technical and 
            organizational measures to protect your information against unauthorized access, alteration, 
            disclosure, or destruction. However, no method of transmission over the Internet or 
            electronic storage is 100% secure.
          </p>
        </section>

        <section className='privacy-section'>
          <h2>6. Your Rights and Choices</h2>
          <p>You have certain rights regarding your information:</p>
          <ul>
            <li><strong>Access:</strong> You can access and update your account information through 
            the application settings.</li>
            <li><strong>Deletion:</strong> You can delete your account and request deletion of your 
            data. However, please note that:
              <ul>
                <li>Some information may be retained for legal or legitimate business purposes</li>
                <li>Content that has been shared with third parties for model training may continue 
                to be used in accordance with this policy</li>
                <li>Public content may remain visible until removed</li>
              </ul>
            </li>
            <li><strong>Opt-Out:</strong> You can opt out of certain data uses, though this may limit 
            your ability to use some features.</li>
            <li><strong>Data Portability:</strong> You may request a copy of your data in a 
            machine-readable format.</li>
          </ul>
          <p>
            <strong>Note:</strong> Due to the nature of machine learning and AI development, photos 
            and images used for model training may have been incorporated into trained models, and 
            complete removal from all systems may not be possible.
          </p>
        </section>

        <section className='privacy-section'>
          <h2>7. Children's Privacy</h2>
          <p>
            Fashioniste is not intended for children under the age of 13. We do not knowingly collect 
            personal information from children under 13. If you are a parent or guardian and believe 
            your child has provided us with personal information, please contact us immediately.
          </p>
        </section>

        <section className='privacy-section'>
          <h2>8. International Data Transfers</h2>
          <p>
            Your information may be transferred to and processed in countries other than your country 
            of residence. These countries may have data protection laws that differ from those in 
            your country. By using Fashioniste, you consent to the transfer of your information to these 
            countries.
          </p>
        </section>

        <section className='privacy-section'>
          <h2>9. Data Retention</h2>
          <p>
            We retain your information for as long as necessary to provide our services, comply with 
            legal obligations, resolve disputes, and enforce our agreements. Photos and images used 
            for model training may be retained for extended periods as they may be incorporated into 
            trained models and datasets.
          </p>
        </section>

        <section className='privacy-section'>
          <h2>10. Changes to This Privacy Policy</h2>
          <p>
            We may update this Privacy Policy from time to time. We will notify you of any changes 
            by posting the new Privacy Policy on this page and updating the "Last Updated" date. 
            You are advised to review this Privacy Policy periodically for any changes.
          </p>
        </section>

        <section className='privacy-section'>
          <h2>11. Contact Us</h2>
          <p>
            If you have any questions about this Privacy Policy or our data practices, please contact us:
          </p>
          <ul>
            <li>Email: baralnishan987@gmail.com</li>
            <li>Through the contact form on our website</li>
          </ul>
        </section>

        <section className='privacy-section'>
          <h2>12. Your Consent</h2>
          <p>
            By using Fashioniste, you consent to this Privacy Policy and agree to its terms. If you do not 
            agree with this policy, please do not use our services.
          </p>
        </section>
      </div>
    </div>
  )
}

export default PrivacyPolicy

